/**
 * Failure-Mode Panel (Day 4).
 *
 * Same SSE protocol as ScenarioRunner — reuses `runScenarioStream` под
 * капотом. Отличие в UI: catalog filter ровно по `group=error-injection`,
 * результаты live-обновляются в карточках one-per-error-code, без полного
 * step-list scrollview'а.
 *
 * Дополнительно — env-config «cheat-sheet» card для `MOCK_FORCE_503`, потому
 * что 5-й error scenario (SERVICE_UNAVAILABLE) требует Gateway-side env
 * флага которого по умолчанию нет. UI явно сообщает «start Gateway with
 * MOCK_FORCE_503=BalanceOf»; раннер не пытается magically настроить
 * Gateway сам (env-only, не runtime — см. mock-adapter.ts:73).
 */
import { useEffect, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { fetchScenarios, scenarioRunUrl } from '../lib/api';
import { cn } from '../lib/cn';
import type {
  ProxyResult,
  ScenarioCompletedEvent,
  ScenarioMeta,
  ScenarioStepEvent,
} from '../lib/types';

interface ParsedSseEvent {
  readonly eventName: string;
  readonly data: unknown;
}

function parseSseFrames(buffer: string): { frames: ParsedSseEvent[]; tail: string } {
  const out: ParsedSseEvent[] = [];
  let tail = buffer;
  while (true) {
    const split = tail.indexOf('\n\n');
    if (split === -1) break;
    const frame = tail.slice(0, split);
    tail = tail.slice(split + 2);
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (dataLines.length === 0) continue;
    try {
      out.push({ eventName, data: JSON.parse(dataLines.join('\n')) });
    } catch {
      // skip malformed
    }
  }
  return { frames: out, tail };
}

interface ScenarioRunState {
  readonly status: 'idle' | 'running' | 'passed' | 'failed' | 'error';
  readonly steps: readonly ScenarioStepEvent[];
  readonly summary?: ScenarioCompletedEvent;
  readonly error?: string;
}

const INITIAL_STATE: ScenarioRunState = { status: 'idle', steps: [] };

const FORCE_503_CHAINCODE_FNS = [
  'CreateCase',
  'CreateContract',
  'GetAgreement',
  'GetContract',
  'SetObjectiveOutcome',
  'AddDeliveryAct',
  'Mint',
  'Burn',
  'BalanceOf',
] as const;

interface BatchProgress {
  /** 1-based index of the scenario currently executing in the batch. */
  readonly current: number;
  readonly total: number;
}

export function FailureModePanel() {
  const [errorScenarios, setErrorScenarios] = useState<readonly ScenarioMeta[]>([]);
  const [runStates, setRunStates] = useState<Record<string, ScenarioRunState>>({});
  const [catalogError, setCatalogError] = useState<string | null>(null);
  /**
   * Non-null while a sequential «Run all» batch is in flight (Issue #49
   * Phase 1). While set: individual Run buttons are disabled and the
   * «Run all» button itself shows `N of M` progress instead of being
   * clickable. Scenarios share Gateway world-state ticks, so running
   * them one-at-a-time keeps timing-sensitive steps (TimeKeeper waits)
   * from interleaving.
   */
  const [batch, setBatch] = useState<BatchProgress | null>(null);
  const abortersRef = useRef<Map<string, AbortController>>(new Map());
  /** Ids of scenarios with an in-flight SSE run (closure-safe mirror of runStates). */
  const runningRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    void (async () => {
      try {
        const cat = await fetchScenarios();
        setErrorScenarios(cat.scenarios.filter((s) => s.group === 'error-injection'));
      } catch (e) {
        setCatalogError((e as Error).message);
      }
    })();
    const aborters = abortersRef.current;
    return () => {
      aborters.forEach((ac) => ac.abort());
    };
  }, []);

  async function runOne(scenarioId: string): Promise<void> {
    // Ref-based guard instead of `runStates[...]` — `runAll` calls this
    // from a loop whose closure sees stale React state.
    if (runningRef.current.has(scenarioId)) return;
    runningRef.current.add(scenarioId);
    setRunStates((prev) => ({ ...prev, [scenarioId]: { status: 'running', steps: [] } }));
    const ac = new AbortController();
    abortersRef.current.set(scenarioId, ac);

    try {
      const res = await fetch(scenarioRunUrl(scenarioId), {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        signal: ac.signal,
      });
      if (!res.ok) {
        setRunStates((prev) => ({
          ...prev,
          [scenarioId]: { status: 'error', steps: [], error: `HTTP ${res.status}` },
        }));
        return;
      }
      const reader = res.body?.getReader();
      if (reader === undefined) {
        setRunStates((prev) => ({
          ...prev,
          [scenarioId]: { status: 'error', steps: [], error: 'no stream body' },
        }));
        return;
      }
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        if (chunk.done) {
          done = true;
          break;
        }
        buffer += decoder.decode(chunk.value, { stream: true });
        const { frames, tail } = parseSseFrames(buffer);
        buffer = tail;
        for (const frame of frames) {
          applyFrame(scenarioId, frame, setRunStates);
        }
      }
      const { frames } = parseSseFrames(buffer + '\n\n');
      for (const frame of frames) applyFrame(scenarioId, frame, setRunStates);
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        setRunStates((prev) => ({
          ...prev,
          [scenarioId]: {
            ...(prev[scenarioId] ?? INITIAL_STATE),
            status: 'error',
            error: 'aborted',
          },
        }));
      } else {
        setRunStates((prev) => ({
          ...prev,
          [scenarioId]: {
            ...(prev[scenarioId] ?? INITIAL_STATE),
            status: 'error',
            error: (e as Error).message,
          },
        }));
      }
    } finally {
      abortersRef.current.delete(scenarioId);
      runningRef.current.delete(scenarioId);
    }
  }

  /**
   * Sequential batch (Issue #49 Phase 1). The old implementation fired
   * every scenario concurrently — their TimeKeeper waits and balance
   * mutations interleaved on the shared Gateway world-state, producing
   * false-red balance asserts that turned green on solo re-run. One
   * aborted/failed scenario does not stop the batch — `runOne` never
   * throws.
   */
  async function runAll(): Promise<void> {
    if (batch !== null || runningRef.current.size > 0) return;
    const list = errorScenarios;
    try {
      for (let i = 0; i < list.length; i++) {
        setBatch({ current: i + 1, total: list.length });
        await runOne(list[i].id);
      }
    } finally {
      setBatch(null);
    }
  }

  const anyRunning = Object.values(runStates).some((s) => s.status === 'running');

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Failure-Mode Panel</h2>
          <p className="text-sm text-slate-500">
            {errorScenarios.length > 0 ? `${String(errorScenarios.length)} ` : ''}error-injection /
            lifecycle scenarios. «Run all» выполняет их последовательно (каждый прогон — на
            одноразовых party-адресах, Issue #49), статус и шаги обновляются live.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void runAll()}
          disabled={errorScenarios.length === 0 || batch !== null || anyRunning}
          className="px-4 py-1.5 rounded bg-brand text-white text-sm font-medium hover:bg-brand-dark disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {batch !== null ? `Running ${batch.current} of ${batch.total}…` : 'Run all'}
        </button>
      </header>

      {catalogError && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load scenario catalog: {catalogError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>⚙️ Gateway env config — MOCK_FORCE_503</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <p>
            The <code>SERVICE_UNAVAILABLE (503)</code> scenario требует Gateway start с CSV
            environment variable <code>MOCK_FORCE_503</code> = одна или несколько chaincode
            functions. Без него Gateway вернёт <code>200 OK</code> и scenario пометит step как{' '}
            <strong>failed</strong> с reason «expected 503, got 200».
          </p>
          <p className="text-xs text-slate-500">
            Env-only — нельзя toggle в runtime. Стопаешь gateway, экспортируешь, запускаешь заново.
          </p>
          <pre className="bg-slate-50 rounded p-2 text-[11px] font-mono overflow-x-auto">
            {`# Restart gateway with one fn (BalanceOf → SERVICE_UNAVAILABLE scenario):
MOCK_FORCE_503=BalanceOf pnpm --filter @nikira/gateway dev

# Multiple fns:
MOCK_FORCE_503=BalanceOf,GetAgreement pnpm --filter @nikira/gateway dev`}
          </pre>
          <div>
            <span className="text-xs text-slate-500">
              Valid CSV values (9 chaincode functions):
            </span>
            <div className="flex flex-wrap gap-1 mt-1">
              {FORCE_503_CHAINCODE_FNS.map((fn) => (
                <code
                  key={fn}
                  className="text-[11px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5"
                >
                  {fn}
                </code>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {errorScenarios.map((s) => {
          const state = runStates[s.id] ?? INITIAL_STATE;
          return (
            <ErrorScenarioCard
              key={s.id}
              meta={s}
              state={state}
              runDisabled={batch !== null}
              onRun={() => void runOne(s.id)}
              onAbort={() => abortersRef.current.get(s.id)?.abort()}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ErrorScenarioCardProps {
  readonly meta: ScenarioMeta;
  readonly state: ScenarioRunState;
  /** True while a «Run all» batch owns the queue — solo runs are blocked. */
  readonly runDisabled: boolean;
  readonly onRun: () => void;
  readonly onAbort: () => void;
}

function ErrorScenarioCard({ meta, state, runDisabled, onRun, onAbort }: ErrorScenarioCardProps) {
  const passed =
    state.summary?.passed_count ?? state.steps.filter((s) => s.status === 'passed').length;
  const failed =
    state.summary?.failed_count ?? state.steps.filter((s) => s.status === 'failed').length;
  const skipped =
    state.summary?.skipped_count ?? state.steps.filter((s) => s.status === 'skipped').length;

  const overall: 'idle' | 'running' | 'passed' | 'failed' | 'error' = state.status;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>{meta.name}</CardTitle>
            <p className="text-xs text-slate-500 mt-1">{meta.summary}</p>
            {meta.notes && <p className="text-xs italic text-amber-700 mt-1">⚠️ {meta.notes}</p>}
          </div>
          <div className="flex items-baseline gap-2 shrink-0">
            <span
              className={cn(
                'text-[10px] uppercase tracking-wide px-2 py-0.5 rounded',
                overallBadge(overall),
              )}
            >
              {overall}
            </span>
            {overall === 'running' ? (
              <button
                type="button"
                onClick={onAbort}
                className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              >
                Abort
              </button>
            ) : (
              <button
                type="button"
                onClick={onRun}
                disabled={runDisabled}
                className="text-xs px-3 py-1 rounded bg-brand text-white hover:bg-brand-dark disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Run
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {state.error !== undefined && (
          <div className="text-xs text-red-700 font-mono">⚠ {state.error}</div>
        )}
        {(state.steps.length > 0 || state.summary !== undefined) && (
          <div className="text-xs text-slate-600">
            {passed} passed · {failed} failed · {skipped} skipped
            {state.summary && <> · {state.summary.elapsed_ms} ms</>}
          </div>
        )}
        {state.steps.map((step) => (
          <ErrorStepRow key={step.step_index} step={step} />
        ))}
      </CardContent>
    </Card>
  );
}

interface ErrorStepRowProps {
  readonly step: ScenarioStepEvent;
}

function ErrorStepRow({ step }: ErrorStepRowProps) {
  const expected = step.expects_error_code;
  const actualCode = extractCode(step.result);
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span
        className={cn('shrink-0 inline-block w-1.5 h-1.5 rounded-full', stripeDotFor(step.status))}
      />
      <span className="text-slate-700 truncate">{step.name}</span>
      {expected !== undefined && (
        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
          expected {expected}
        </span>
      )}
      {actualCode !== undefined && actualCode !== expected && (
        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">
          got {actualCode}
        </span>
      )}
      {step.failure_reason && (
        <span className="shrink-0 text-[10px] text-red-700 italic">{step.failure_reason}</span>
      )}
      {step.elapsed_ms !== undefined && (
        <span className="shrink-0 ml-auto text-slate-400">{step.elapsed_ms} ms</span>
      )}
    </div>
  );
}

function extractCode(result: ProxyResult | undefined): string | undefined {
  if (result === undefined) return undefined;
  if (result.proxy_status !== 'ok') return undefined;
  const body = result.gateway_body;
  if (body === null || typeof body !== 'object') return undefined;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== 'object') return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function overallBadge(s: ScenarioRunState['status']): string {
  switch (s) {
    case 'idle':
      return 'bg-slate-100 text-slate-600';
    case 'running':
      return 'bg-sky-100 text-sky-800 animate-pulse';
    case 'passed':
      return 'bg-emerald-100 text-emerald-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'error':
      return 'bg-amber-100 text-amber-800';
  }
}

function stripeDotFor(s: ScenarioStepEvent['status']): string {
  switch (s) {
    case 'queued':
      return 'bg-slate-300';
    case 'running':
      return 'bg-sky-500 animate-pulse';
    case 'passed':
      return 'bg-emerald-500';
    case 'failed':
      return 'bg-red-500';
    case 'skipped':
      return 'bg-amber-400';
  }
}

function applyFrame(
  scenarioId: string,
  ev: ParsedSseEvent,
  setRunStates: React.Dispatch<React.SetStateAction<Record<string, ScenarioRunState>>>,
): void {
  setRunStates((prev) => {
    const current = prev[scenarioId] ?? INITIAL_STATE;
    if (ev.eventName === 'step.started' || ev.eventName === 'step.completed') {
      const step = ev.data as ScenarioStepEvent;
      const next = [...current.steps];
      const idx = next.findIndex((s) => s.step_index === step.step_index);
      if (idx === -1) next.push(step);
      else next[idx] = step;
      next.sort((a, b) => a.step_index - b.step_index);
      return { ...prev, [scenarioId]: { ...current, steps: next } };
    }
    if (ev.eventName === 'scenario.completed') {
      const summary = ev.data as ScenarioCompletedEvent;
      return {
        ...prev,
        [scenarioId]: {
          ...current,
          summary,
          status: summary.status,
        },
      };
    }
    return prev;
  });
}
